import React, { useState, useEffect, useCallback } from 'react';
import { accountsAPI } from '../lib/api';
import { businessToday } from '../lib/period';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Handshake, Plus, History, ChevronRight, Percent, Clock,
  ArrowRightLeft, CreditCard, Store, Truck, Edit2,
} from 'lucide-react';
import { toast } from 'sonner';

const MODEL_LABELS = {
  DELIVERY: 'Delivery',
  DINE_IN_PARTNER_PAY: 'Dine-In Pay',
  CARD_MDR: 'Card MDR',
  DIRECT: 'Direct',
};

const MODEL_ICONS = {
  DELIVERY: Truck,
  DINE_IN_PARTNER_PAY: Store,
  CARD_MDR: CreditCard,
  DIRECT: ArrowRightLeft,
};

const PARTNER_COLORS = {
  ZOMATO: 'bg-red-500/10 text-red-400 border-red-500/20',
  SWIGGY: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  BANK: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  NONE: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ─── Config Editor Dialog ───
const ConfigEditor = ({ open, onClose, onSave, editing }) => {
  const [form, setForm] = useState({
    partner: '', commercial_model: '', commission_pct: '',
    gst_on_commission_pct: '18', payment_terms_days: '2',
    effective_from: businessToday(), label: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        partner: editing.partner || '',
        commercial_model: editing.commercial_model || '',
        commission_pct: String(editing.commission_pct ?? ''),
        gst_on_commission_pct: String(editing.gst_on_commission_pct ?? '18'),
        payment_terms_days: String(editing.payment_terms_days ?? '2'),
        effective_from: businessToday(),
        label: editing.label || '',
      });
    } else {
      setForm({
        partner: '', commercial_model: '', commission_pct: '',
        gst_on_commission_pct: '18', payment_terms_days: '2',
        effective_from: businessToday(), label: '',
      });
    }
  }, [editing, open]);

  const handleSave = async () => {
    if (!form.partner || !form.commercial_model || !form.commission_pct) {
      toast.error('Partner, model, and commission % are required');
      return;
    }
    setSaving(true);
    try {
      await accountsAPI.createCommissionConfig({
        partner: form.partner,
        commercial_model: form.commercial_model,
        commission_pct: parseFloat(form.commission_pct),
        gst_on_commission_pct: parseFloat(form.gst_on_commission_pct || '18'),
        payment_terms_days: parseInt(form.payment_terms_days || '2'),
        effective_from: form.effective_from,
        label: form.label || `${form.partner} ${MODEL_LABELS[form.commercial_model] || form.commercial_model}`,
      });
      toast.success('Commission config saved');
      onSave();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800" data-testid="config-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">
            {editing ? 'Update Commission Config' : 'New Commission Config'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Partner</Label>
              <Select value={form.partner} onValueChange={v => setForm(f => ({...f, partner: v}))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="partner-select">
                  <SelectValue placeholder="Select partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZOMATO">Zomato</SelectItem>
                  <SelectItem value="SWIGGY">Swiggy</SelectItem>
                  <SelectItem value="BANK">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Commercial Model</Label>
              <Select value={form.commercial_model} onValueChange={v => setForm(f => ({...f, commercial_model: v}))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="model-select">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DELIVERY">Delivery</SelectItem>
                  <SelectItem value="DINE_IN_PARTNER_PAY">Dine-In Pay</SelectItem>
                  <SelectItem value="CARD_MDR">Card MDR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Commission %</Label>
              <Input type="number" step="0.1" value={form.commission_pct}
                onChange={e => setForm(f => ({...f, commission_pct: e.target.value}))}
                className="bg-zinc-800 border-zinc-700" data-testid="commission-pct-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">GST on Commission %</Label>
              <Input type="number" step="0.1" value={form.gst_on_commission_pct}
                onChange={e => setForm(f => ({...f, gst_on_commission_pct: e.target.value}))}
                className="bg-zinc-800 border-zinc-700" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Settlement Days</Label>
              <Input type="number" value={form.payment_terms_days}
                onChange={e => setForm(f => ({...f, payment_terms_days: e.target.value}))}
                className="bg-zinc-800 border-zinc-700" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Effective From</Label>
              <Input type="date" value={form.effective_from}
                onChange={e => setForm(f => ({...f, effective_from: e.target.value}))}
                className="bg-zinc-800 border-zinc-700" data-testid="effective-from-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Label</Label>
              <Input value={form.label}
                onChange={e => setForm(f => ({...f, label: e.target.value}))}
                placeholder="Auto-generated if blank"
                className="bg-zinc-800 border-zinc-700" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? 'Saving...' : editing ? 'Update Config' : 'Create Config'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Version History Panel ───
const VersionHistory = ({ partner, model, open, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !partner || !model) return;
    setLoading(true);
    accountsAPI.getCommissionHistory({ partner, commercial_model: model })
      .then(r => setHistory(r.data?.history || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [open, partner, model]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-zinc-900 border-zinc-800" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <History className="w-4 h-4" />
            Version History — {partner} {MODEL_LABELS[model] || model}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
          ) : history.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">No version history</p>
          ) : (
            history.map((v, i) => (
              <div key={v.config_id || i}
                className={`rounded-lg border p-3 ${v.is_active ? 'border-emerald-500/30 bg-emerald-950/20' : 'border-zinc-800 bg-zinc-800/30'}`}
                data-testid={`history-version-${v.version}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-zinc-400">v{v.version}</span>
                    {v.is_active && <Badge className="bg-emerald-500/20 text-emerald-400 text-[9px]">ACTIVE</Badge>}
                    {v.is_default_seed && <Badge variant="outline" className="text-[9px] text-zinc-500">SEED</Badge>}
                  </div>
                  <span className="text-lg font-mono font-bold text-zinc-100">{v.commission_pct}%</span>
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-[11px] text-zinc-500">
                  <span>GST: {v.gst_on_commission_pct}%</span>
                  <span>Settlement: T+{v.payment_terms_days}</span>
                  <span>From: {fmtDate(v.effective_from)}</span>
                  {v.effective_to && <span>To: {fmtDate(v.effective_to)}</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-600">
                  <span>Created: {fmtDate(v.created_at)}</span>
                  {v.created_by && <span>By: {v.created_by}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Config Card ───
const ConfigCard = ({ config, onEdit, onHistory }) => {
  const ModelIcon = MODEL_ICONS[config.commercial_model] || ArrowRightLeft;
  const partnerColor = PARTNER_COLORS[config.partner] || PARTNER_COLORS.NONE;
  const modelLabel = MODEL_LABELS[config.commercial_model] || config.commercial_model;

  const effectiveRate = config.commission_pct;
  const totalDeduction = effectiveRate + (effectiveRate * (config.gst_on_commission_pct || 18) / 100);

  return (
    <Card className="bg-zinc-900/60 border-zinc-800/60 hover:border-zinc-700/60 transition-colors"
      data-testid={`config-card-${config.partner}-${config.commercial_model}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${partnerColor}`}>
              <ModelIcon className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-zinc-100">{config.partner}</span>
                <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-400">
                  {modelLabel}
                </Badge>
              </div>
              <p className="text-[11px] text-zinc-500 mt-0.5">{config.label}</p>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xl font-mono font-bold text-zinc-100" data-testid="commission-rate">
              {config.commission_pct}%
            </div>
            <p className="text-[10px] text-zinc-500">
              +{config.gst_on_commission_pct}% GST = {totalDeduction.toFixed(1)}% total
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/60">
          <div className="flex items-center gap-4 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> T+{config.payment_terms_days}d
            </span>
            <span>v{config.version}</span>
            <span>From {fmtDate(config.effective_from)}</span>
            {config.is_default_seed && (
              <Badge variant="outline" className="text-[8px] text-zinc-600 border-zinc-700">SEED</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm"
              className="h-7 px-2 text-zinc-400 hover:text-zinc-200"
              onClick={() => onHistory(config.partner, config.commercial_model)}
              data-testid="history-btn">
              <History className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm"
              className="h-7 px-2 text-zinc-400 hover:text-zinc-200"
              onClick={() => onEdit(config)}
              data-testid="edit-config-btn">
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ───
export default function PartnersPage() {
  const [configs, setConfigs] = useState([]);
  const [blocker, setBlocker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState({ partner: '', model: '' });

  const loadConfigs = useCallback(async () => {
    try {
      const res = await accountsAPI.getCommissionConfigs({ active_only: true });
      setConfigs(res.data?.configs || []);
      setBlocker(res.data?.blocker || null);
    } catch (e) {
      toast.error('Failed to load commission configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleEdit = (config) => {
    setEditing(config);
    setEditorOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const handleHistory = (partner, model) => {
    setHistoryTarget({ partner, model });
    setHistoryOpen(true);
  };

  // Group configs by partner
  const grouped = configs.reduce((acc, c) => {
    const key = c.partner;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const partnerOrder = ['ZOMATO', 'SWIGGY', 'BANK'];
  const sortedPartners = Object.keys(grouped).sort(
    (a, b) => (partnerOrder.indexOf(a) === -1 ? 99 : partnerOrder.indexOf(a))
           - (partnerOrder.indexOf(b) === -1 ? 99 : partnerOrder.indexOf(b))
  );

  return (
    <div className="space-y-6" data-testid="partners-page">
      {blocker && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="commission-ownership-blocker">
          <b>Commission changes are paused.</b> {blocker.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2.5">
            <Handshake className="w-6 h-6 text-emerald-400" />
            Partner & Commission Management
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Commercial models, commission rates, and settlement terms
          </p>
        </div>
        <Button onClick={handleNew} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          data-testid="new-config-btn">
          <Plus className="w-4 h-4" /> New Config
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Active Configs', value: configs.length, sub: 'across all partners' },
          { label: 'Partners', value: sortedPartners.length, sub: 'with active configs' },
          { label: 'Avg Commission', value: configs.length > 0
              ? (configs.reduce((s, c) => s + c.commission_pct, 0) / configs.length).toFixed(1) + '%'
              : '0%', sub: 'weighted by config' },
          { label: 'Models', value: [...new Set(configs.map(c => c.commercial_model))].length, sub: 'commercial types' },
        ].map((s) => (
          <Card key={s.label} className="bg-zinc-900/40 border-zinc-800/40">
            <CardContent className="p-3">
              <p className="text-[11px] text-zinc-500">{s.label}</p>
              <p className="text-xl font-mono font-bold text-zinc-100 mt-0.5">{s.value}</p>
              <p className="text-[10px] text-zinc-600">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Partner sections */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : configs.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="py-12 text-center">
            <Handshake className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No commission configs yet</p>
            <p className="text-xs text-zinc-600 mt-1">Add your first partner commission config to get started</p>
            <Button onClick={handleNew} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
              <Plus className="w-4 h-4" /> Create Config
            </Button>
          </CardContent>
        </Card>
      ) : (
        sortedPartners.map(partner => (
          <div key={partner} className="space-y-2" data-testid={`partner-section-${partner}`}>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${PARTNER_COLORS[partner]} text-xs`}>{partner}</Badge>
              <ChevronRight className="w-3 h-3 text-zinc-600" />
              <span className="text-xs text-zinc-500">
                {grouped[partner].length} active config{grouped[partner].length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {grouped[partner].map(config => (
                <ConfigCard key={config.config_id} config={config}
                  onEdit={handleEdit} onHistory={handleHistory} />
              ))}
            </div>
          </div>
        ))
      )}

      {/* Editor Dialog */}
      <ConfigEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        onSave={loadConfigs}
        editing={editing}
      />

      {/* Version History Dialog */}
      <VersionHistory
        partner={historyTarget.partner}
        model={historyTarget.model}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
